/**
 * CreateClassModal - Form modal for creating/editing classes
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Class, CreateClassRequest, UpdateClassRequest } from '@/types/class';

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateClassRequest | UpdateClassRequest) => Promise<void>;
  initialData?: Class;
}

const PRESET_COLORS = [
  '#12356B', // Navy
  '#367A25', // Green
  '#DD6832', // Orange
  '#CED4DE', // Gray Alt
  '#0d2850', // Navy Dark
  '#2d6820', // Green Dark
  '#c4562a', // Orange Dark
  '#E2E5EC', // Light Gray
];

export function CreateClassModal({ isOpen, onClose, onSubmit, initialData }: CreateClassModalProps) {
  const [className, setClassName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!initialData;

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setClassName(initialData.className);
      setDescription(initialData.description || '');
      setColor(initialData.color || PRESET_COLORS[0]);
    } else {
      // Reset form when creating new
      setClassName('');
      setDescription('');
      setColor(PRESET_COLORS[0]);
    }
    setError('');
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate className
    if (!className.trim()) {
      setError('Class name is required');
      return;
    }

    if (className.length > 100) {
      setError('Class name must be 100 characters or less');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = {
        className: className.trim(),
        description: description.trim() || undefined,
        color,
      };

      await onSubmit(data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save class');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="border-[#12356B] text-[#12356B] hover:bg-[#E2E5EC] hover:text-[#12356B]">
        Cancel
      </Button>
      <Button type="submit" form="class-form" disabled={isSubmitting} className="bg-[#367A25] text-white hover:bg-[#2d6820]">
        {isSubmitting ? 'Saving...' : isEditMode ? 'Update Class' : 'Create Class'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Class' : 'Create New Class'}
      footer={footer}
    >
      <form id="class-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Class Name */}
        <div>
          <label htmlFor="className" className="block text-sm font-medium text-gray-700 mb-1">
            Class Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="className"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            placeholder="e.g., Period 3 Biology"
            maxLength={100}
            className="w-full px-3 py-2 border border-[#CED4DE] rounded-md focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B]"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {className.length}/100 characters
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Morning biology class"
            rows={3}
            className="w-full px-3 py-2 border border-[#CED4DE] rounded-md focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B] resize-none"
          />
        </div>

        {/* Color Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color
          </label>
          <div className="flex gap-2 flex-wrap">
            {PRESET_COLORS.map((presetColor) => (
              <button
                key={presetColor}
                type="button"
                onClick={() => setColor(presetColor)}
                className={`w-10 h-10 rounded-full border-2 transition-all ${
                  color === presetColor ? 'border-[#12356B] scale-110' : 'border-[#CED4DE]'
                }`}
                style={{ backgroundColor: presetColor }}
                aria-label={`Select color ${presetColor}`}
              />
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
